import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { ENDPOINTS } from '../constants/apiEndpoints';
import { apiFetch, ApiError } from '../lib/api';
import { readSessionExpiry, readUser, writeSession } from '../lib/storage';

// Rejects with a user-facing message string.
export const loginUser = createAsyncThunk('auth/login', async (credentials, { rejectWithValue }) => {
  try {
    // auth: true so the browser stores the httpOnly session cookie it sets.
    const data = await apiFetch(ENDPOINTS.AUTH_LOGIN, { method: 'POST', body: credentials });
    if (!data?.user || !data?.expiresAt) return rejectWithValue('Invalid response format');
    writeSession(data.user, data.expiresAt);
    return { user: data.user, expiresAt: data.expiresAt };
  } catch (err) {
    if (err instanceof ApiError) return rejectWithValue(err.data?.message || err.data?.error || 'Login failed');
    if (err instanceof TypeError) return rejectWithValue('Network error. Please check your connection.');
    return rejectWithValue(err.message || 'An unexpected error occurred');
  }
});


export const resetPassword = createAsyncThunk(
  "auth/resetPassword",
  async (email, { rejectWithValue }) => {
    try {
      const data = await apiFetch(ENDPOINTS.AUTH_RESET_PASSWORD, {
        method: "POST",
        body: { email },
      });

      return data;
    } catch (err) {
      if (err instanceof ApiError) {
        return rejectWithValue(
          err.data?.message ||
          err.data?.error ||
          "Unable to process password reset request."
        );
      }

      if (err instanceof TypeError) {
        return rejectWithValue(
          "Network error. Please check your connection."
        );
      }

      return rejectWithValue(
        err.message || "An unexpected error occurred."
      );
    }
  }
);

export const updatePassword = createAsyncThunk(
  'auth/updatePassword',
  async ({ currentPassword, newPassword }, { getState, rejectWithValue }) => {
    if (!getState().auth.user) return rejectWithValue('Session expired. Please login again.');
    try {
      const data = await apiFetch(ENDPOINTS.AUTH_UPDATE_PASSWORD, {
        method: 'POST',
        body: { currentPassword, newPassword },
      });
      if (!data?.success) return rejectWithValue(data?.message || 'Failed to update password');
      return data;
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to update password. Please try again.');
    }
  },
);

const authSlice = createSlice({
  name: 'auth',
  initialState: () => ({ user: readUser(), expiresAt: readSessionExpiry() }),
  reducers: {
    sessionCleared: (state) => {
      state.user = null;
      state.expiresAt = null;
    },
    // payload: { user, expiresAt } as read from localStorage after another tab changed it
    sessionSynced: (state, { payload }) => {
      state.user = payload.user;
      state.expiresAt = payload.expiresAt;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(loginUser.fulfilled, (state, { payload }) => {
      state.user = payload.user;
      state.expiresAt = payload.expiresAt;
    });
  },
});

export const { sessionCleared, sessionSynced } = authSlice.actions;
// True while a session is believed active (the server still has the final say).
export const selectIsLoggedIn = (state) =>
  Boolean(state.auth.user) && Number(state.auth.expiresAt) > Date.now();
export const selectUser = (state) => state.auth.user;
export default authSlice.reducer;
